import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import cpgSquare from './../../assets/images/cpg-shapes-square.svg';
import cpgTriangle from './../../assets/images/cpg-shapes-triangle.svg';
import barChart from './../../assets/images/cpg-bar-chart.svg';
import '../cpg.scss';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

export default function CPGUsage() {

  const square1Ref = useRef();
  const square2Ref = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.height = fill + '%';
          }

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }

        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(square1Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(square2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => ctx.revert(); // <- cleanup!

    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  return (
    <>
      <div className="cpg-usage">

        <p id="s1" className="intro" data-aos="fade-up">Explore key stats and insights to see how today’s CPG companies are making use of direct mail.&#8288;<a href="#reference-1" class="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-left'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Marketing Effectiveness</h3>

                <p className="pull-quote"><span data-aos data-aos-once="true" className='highlighter'>80%</span> of CPG companies report that direct mail is <span data-aos data-aos-once="true" className='highlighter'>very or extremely effective.</span></p>

                <p>They find direct mail to be more effective than social media, newspaper/magazine, radio, TV, magazine and billboard/outdoor ads.</p>

                <p>CPG companies find paid search to be the most effective channel.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Direct mail is most effective when used as a part of omnichannel campaigns. Making use of multiple mediums is important, but without proper integration, marketing may end up disjointed, disconnected and ineffectual. <Link to="/cpg/integration#top">Explore the Integration section</Link> for insights and tips on creating seamless, well-connected campaigns.</p>
              </div>
            </div>

            <div className='inner-cols'>
              <div className='inner-col-item'>
                <div className='rounded-box-module-wrap'>
                  <div className="rounded-box-module" data-aos="fade-up">
                    <h3>Campaigns Using Direct Mail</h3>
                    <div className='progress-bar-area' data-aos data-aos-id="bar-anim">
                      <div className='progressbar-item'>
                        <span className='label'>More than 50% of campaigns</span>
                        <div className='progress-bar size-small'>
                          <div className='progress' data-fill="31"><span class="percentage" data-to="31" aria-hidden="true">0</span><span class="screen-reader-text">31</span>%</div>
                        </div>
                      </div>
                      <div className='progressbar-item'>
                        <span className='label'>Between 25% and 49% of campaigns</span>
                        <div className='progress-bar size-small'>
                          <div className='progress fill-light' data-fill="54"><span class="percentage" data-to="54" aria-hidden="true">0</span><span class="screen-reader-text">54</span>%</div>
                        </div>
                      </div>
                      <div className='progressbar-item'>
                        <span className='label'>Less than 25% of campaigns</span>
                        <div className='progress-bar size-small'>
                          <div className='progress fill-light' data-fill="14"><span class="percentage" data-to="14" aria-hidden="true">0</span><span class="screen-reader-text">14</span>%</div>
                        </div>
                      </div>
                    </div>
                    <p> CPG companies use direct mail in a higher percentage of campaigns than any other industry does.</p>
                  </div>
                  <div className="rounded-box-module">
                    <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                    <p>Maintaining a diverse mix of marketing channels offers customers multiple ways to engage while building brand recognition. This also allows for better data collection: The more channels you use, the more consumer data you have, which can then be employed to create more personalized, relevant campaigns.</p>
                  </div>
                </div>
              </div>
              <div className='inner-col-item'>

                <div className='rounded-box-module-wrap'>
                  <div className="rounded-box-module" data-aos="fade-up">
                    <h3>Direct Mail Techniques</h3>
                    
                    
                    <div className='progress-bar-area' data-aos data-aos-id="bar-anim">
                      <div className='progressbar-item'>
                        <span className='label'>Use of location imagery</span>
                        <div className='progress-bar size-small'>
                          <div className='progress' data-fill="66"><span class="percentage" data-to="66" aria-hidden="true">0</span><span class="screen-reader-text">66</span>%</div>
                        </div>
                      </div>
                      <div className='progressbar-item'>
                        <span className='label'>Personalized offers</span>
                        <div className='progress-bar size-small'>
                          <div className='progress' data-fill="66"><span class="percentage" data-to="66" aria-hidden="true">0</span><span class="screen-reader-text">66</span>%</div>
                        </div>
                      </div>
                    </div>
                    <p>Among consumer packaged goods companies, use of business-location imagery and personalized offers are the most common direct mail techniques. In fact, CPG businesses are more likely than businesses in other industries to use location-based imagery.</p>
                  </div>
                  <div className="rounded-box-module">
                    <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                    <p>Including location-based imagery in direct mail can help create a sense of familiarity off the bat—especially if you include images of well-known local landmarks or places customers are likely to feel a connection to. If you sell outdoor and adventure gear, for example, including an image of a popular local hiking spot may help grab recipients’ attention.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-right'>
            
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Direct Mail and the Customer Journey</h3>

                <div className='progress-bar-area vertical-bar-chart' data-aos data-aos-id="bar-anim" data-aos-offset="300">
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="43"><span class="percentage" data-to="43" aria-hidden="true">0</span><span class="screen-reader-text">43</span>%</div>
                    </div>
                    <span className='label'>Drive awareness</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress' data-fill="17"><span class="percentage" data-to="17" aria-hidden="true">0</span><span class="screen-reader-text">17</span>%</div>
                    </div>
                    <span className='label'>Drive growth</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="26"><span class="percentage" data-to="26" aria-hidden="true">0</span><span class="screen-reader-text">26</span>%</div>
                    </div>
                    <span className='label'>Drive leads</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="14"><span class="percentage" data-to="14" aria-hidden="true">0</span><span class="screen-reader-text">14</span>%</div>
                    </div>
                    <span className='label'>Drive loyalty</span>
                  </div>
                </div>

                <p>In relation to the customer journey, CPG companies report using mail primarily to drive awareness—and are more likely than businesses in other industries to do so. They’re also the most likely to use mail to drive leads, and the least likely to use it to drive loyalty.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>The tangible quality of direct mail can help grab customers’ attention and increase brand awareness. In the age of digital, most people are exposed to countless ads every day—whether on the computer, TV or smartphone. Direct mail can break through the digital clutter to provide a more immediate and personal experience.</p>
              </div>
            </div>
            <div className='personalized-box secondary'>
              <h3>Many CPG companies use location-based imagery in their direct mail.</h3>
              <div data-aos data-aos-id="vector-shapes" className='shape-images'>
                <div className='triangle triangle-1'>
                  <img ref={triangle1Ref} src={cpgTriangle} alt="" />
                </div>
                <div className='square square-1'>
                  <img ref={square1Ref} src={cpgSquare} alt="" />
                </div>
                <div className='triangle triangle-2'>
                  <img ref={triangle2Ref} src={cpgTriangle} alt="" />
                </div>
                <div className='square square-2'>
                  <img ref={square2Ref} src={cpgSquare} alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
